import styled, { DefaultTheme } from 'styled-components';
import { Card, Spin } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const dark = (p: { theme?: DefaultTheme }, darkVal: string, lightVal: string): string =>
  p.theme?.mode === 'dark' ? darkVal : lightVal;

export const PageWrapper = styled.div`
  padding: 24px;
  background: ${(p) => dark(p, '#0f1419', '#f5f6f8')};
`;

export const HeaderWrap = styled.header`
  text-align: center;
  margin-bottom: 32px;
`;

export const PageTitle = styled.h1`
  margin: 0 0 8px 0;
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  font-weight: 700;
  background: linear-gradient(90deg, #2563eb, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

export const StatCard = styled(Card)`
  border-radius: 16px;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${(p) => (p.theme?.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)')};
  }
`;

export const StatIconWrap = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  margin-top: 4px;
`;

export const StatHelp = styled.div<{ $color: string }>`
  font-size: 0.75rem;
  color: ${(p) => p.$color};
  margin-top: 2px;
`;

export const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SectionCard = styled(Card)`
  border-radius: 16px;
  overflow: hidden;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }

  .ant-card-body {
    padding: 0;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const SectionContent = styled.div`
  padding: 16px 24px 24px;
`;

export const TableWrapper = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    background: ${(p) => dark(p, '#334155', '#f1f5f9')} !important;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')} !important;
    font-weight: 600;
    font-size: 0.8125rem;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    color: ${(p) => dark(p, '#e2e8f0', '#334155')};
  }
  .ant-table-wrapper .ant-table-tbody > tr:hover > td {
    background: ${(p) => dark(p, '#334155', '#f8fafc')} !important;
  }
`;

export const EmptyWrap = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

export const EmptyIconWrap = styled.div`
  margin-bottom: 12px;
  color: ${(p) => dark(p, '#64748b', '#94a3b8')};
  font-size: 2rem;
`;

export const EmptyText = styled.div`
  font-size: 1rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const EmptySubText = styled.div`
  font-size: 0.8125rem;
  color: ${(p) => dark(p, '#64748b', '#94a3b8')};
  margin-top: 4px;
`;

export const MobileCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 12px;

  &.ant-card {
    background: ${(p) => dark(p, '#1e293b', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#334155', '#e2e8f0')};
  }
`;

export const MobileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${(p) => dark(p, '#334155', '#f1f5f9')};

  &:last-child {
    border-bottom: none;
  }
`;

export const MobileLabel = styled.span`
  font-weight: 600;
  color: ${(p) => dark(p, '#cbd5e1', '#475569')};
  font-size: 0.8125rem;
`;

export const MobileValue = styled.span`
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  font-size: 0.875rem;
`;

export const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`;

export const LoadingText = styled.span`
  font-size: 0.9375rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const StyledSpin = styled(Spin)`
  .ant-spin-dot-item {
    background-color: ${(p) => dark(p, '#3b82f6', '#2563eb')};
  }
`;
